import {get, map} from 'lodash'
import {useContext, useEffect, useState} from "react";
import {DataGridWrapper} from "../../../../Utils/DataGridWrapper/DataGridWrapper";
import {getAllOrganizationsUsersAsCSV, getOrganizationDeletedUsersApi} from "../../../../Services/api";
import {GridReactContextProvider} from "../../../../Utils/DataGridWrapper/GridContext";
import {useUserAdminOrgs} from "../../../Hooks/UseUserAdminOrgs";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {UserDetailsCell} from "../UserDetailsCell/UserDetailsCell";
import {format} from "date-fns";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {useNavigate} from "react-router-dom";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {UserReactContext} from "../../../../contexts/UserContext";
import {PhoneCell} from "../../../../Utils/PhoneCell/PhoneCell";
import {RestoreUsersTableButton} from "../../../BulkRestoreUsers/RestoreUsersTableButton";

const getDeletedUsersColumns = ({i18n}) => {
    return [
        {
            field: 'firstName',
            headerName: i18n.translate("deletedUserTable.columns.fullName"),
            minWidth: 160,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'phoneFormatted',
            headerName: i18n.translate("deletedUserTable.columns.phoneNumber"),
            minWidth: 150,
            align: 'left',
            sortable: false,
            renderCell: (params) => <PhoneCell value={params.value} />,
        },
        {
            field: 'email',
            headerName: i18n.translate("deletedUserTable.columns.email"),
            minWidth: 300,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value ? params.value : i18n.translate("deletedUserTable.emailNotAssigned")}</div>,
        },
        {
            field: 'firstLoginDate',
            headerName: i18n.translate("deletedUserTable.columns.firstLoginDate"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'deletedDate',
            headerName: i18n.translate("deletedUserTable.columns.deletedDate"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'userDetails',
            headerName: i18n.translate("deletedUserTable.columns.userDetails"),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => <UserDetailsCell user={params.row}/>,
        },
    ];
}

const tableFilters = {
    showFilters: {
        search: true,
        organizations: true
    },
    filterValues: {},
    filterData: {}
}

const convertData = (users) => {
    return map(users, (user) => {
        user.deletedDate = format(user.deletedAt, 'dd/MM/yyyy');
        if (user.loginData && user.loginData.firstLoginDate) {
            user.firstLoginDate = format(user.loginData.firstLoginDate, 'dd/MM/yyyy')
        }
        return user
    })
}

const paginationModel = {
    page: 0,
    pageSize: 100,
}

export default function DeletedUserTable() {
    const {adminOrganizationOptions} = useUserAdminOrgs({withAllOption: true, withCoursesAndSignDocs: false})
    const [localTableFilters, setLocalTableFilters] = useState(null)
    const [isDownloading, setIsDownloading] = useState(false)
    const {i18n} = useContext(I18nReactContext);
    const userContext = useContext(UserReactContext);
    const isCompanyAdmin = userContext.isUserCompanyAdmin();
    const navigate = useNavigate();

    useEffect(() => {
        if (adminOrganizationOptions) {
            setLocalTableFilters({...tableFilters,
                filterData: {
                    ...tableFilters.filterData,
                    organizations: adminOrganizationOptions,
                },
                filterValues: {
                    ...tableFilters.filterValues,
                    organizationIds: [get(adminOrganizationOptions, '[0].value')]
                }})
        }
    }, [adminOrganizationOptions]);

    const downloadDeletedUsersAsCSV = async ({organizationIds}) => {
        setIsDownloading(true)
        try {
            await getAllOrganizationsUsersAsCSV({organizationIds, deletedUsers: true})
        } finally {
            setIsDownloading(false)
        }
    }

    const buttons = [
        {
            type: 'MultipleOptionsButton',
            text: i18n.translate('deletedUsersPage.buttons.downloadCsv'),
            icon: <CloudDownloadIcon/>,
            loading: isDownloading,
            disabled: isDownloading,
            options: [
                {
                    text: i18n.translate('deletedUsersPage.buttons.downloadCsvForOrganizations'),
                    disabled: isDownloading,
                    action: async ({filterModel}) => {
                        const selectedOrgIds = get(filterModel, 'filterValues.organizationIds', [])
                        // If "all" is selected, pass empty array to get all organizations
                        const organizationIds = selectedOrgIds.includes('all') ? [] : selectedOrgIds
                        try {
                            await downloadDeletedUsersAsCSV({organizationIds})
                            navigate('/reports/generatedReports')
                        } catch (error) {
                            console.error('Error downloading deleted users CSV:', error)
                        }
                    }
                },
            ]
        },
    ]

    // Company Admins get an extra Restore button + row checkboxes; non-CAs see neither.
    if (isCompanyAdmin) {
        buttons.push({
            type: 'ComponentButton',
            component: () => <RestoreUsersTableButton onSuccess={() => {}}/>
        })
    }

    const getColumns = async () => {
        return getDeletedUsersColumns({i18n})
    }
    const apiToGetData = async ({paginationModel, filterModel, sortModel}) => {
        return getOrganizationDeletedUsersApi({
            paginationModel,
            filterModel,
            sortModel
        })
    }

    return <GridReactContextProvider>
                {localTableFilters ?  <DataGridWrapper
                    filterModel={localTableFilters}
                    disableColumnFilter
                    paginationModel={paginationModel}
                    getColumns={getColumns}
                    apiToGetData={apiToGetData}
                    dataConverter={convertData}
                    buttons={buttons}
                    sortModel={null}
                    checkboxSelection={isCompanyAdmin}
                    onSelectionModelChange={() => {}}
                /> :   <LoadingSpinner/>}
        </GridReactContextProvider>
}